"use client";
import useSWR from "swr";
import GraphPreview from "@ouch-frontend/graph-editor";
import "@ouch-frontend/graph-editor/style.css";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { cn, getFileDownloadAdress, getSuffixByFileName } from "@/lib/utils";
import { fetchCollectionsData } from "@/lib/fetchData";
import { useLang, useLangData, useSite } from "@/hooks/useSiteInfo";
import {
  Accordion,
  AccordionItem,
  AccordionContent,
  AccordionTrigger,
} from "@/components/ui/accordion";
import RouterLink from "@/components/ui/router-link";
import { X, Dot } from "lucide-react";
import { generateLevelParams } from "@/lib/cateogry";
import { useScopedI18n } from "@/messages/client";
import { uniqBy } from "lodash-es";
import { listToTree } from "@/lib/tree";
import { Button } from "@/components/ui/button";
import { useUser } from "@/components/clients/LocaleToStorage";
import { usePathname } from "next/navigation";
import GoLoginSVG from "./go-login.svg";
import NoDataSVG from "./no-data.svg";
import Image from "next/image";

type CategoryItem = {
  name: string;
  id: number;
  level: number;
  parent: CategoryItem;
  lang?: number;
  lang_data?: {
    lang: number;
    name: string;
  }[];
};

type DirectusFile = {
  id: string;
  filename_disk: string;
  filename_download: string;
  title: string
};

type Product = {
  material_number: string;
  id: number;
  category: CategoryItem;
  slug: string;
  summary: string;
  datasheet1_text?: string;
  datasheet1_file?: DirectusFile;
  datasheet2_text?: string;
  datasheet2_file?: DirectusFile;
  datasheet3_text?: string;
  datasheet3_file?: DirectusFile;
  application_file?: DirectusFile;
  evm_file?: DirectusFile;
};

type CategoryTreeItem = CategoryItem & {
  products?: Product[];
  children?: CategoryTreeItem[];
};

function getCategoryName(category: CategoryItem, lang?: number) {
  if (lang && category.lang !== lang) {
    const langData = category.lang_data?.find((el) => el.lang === lang);
    if (langData) {
      return langData.name;
    }
  }
  return category.name;
}

function getCategoryByLevel(category: CategoryItem, lang?: number) {
  const arr: CategoryItem[] = [];
  if (category.level > 2 && category.parent) {
    const mergeCategory = {
      id: category.id,
      level: category.level,
      parent: category.parent.parent,
      name: `${getCategoryName(category.parent, lang)}/${getCategoryName(
        category,
        lang
      )}`,
    };
    arr.push(mergeCategory);
    arr.push(...getCategoryByLevel(mergeCategory.parent, lang));
  } else {
    arr.push(category);
    if (category.parent) {
      arr.push(...getCategoryByLevel(category.parent, lang));
    }
  }
  return arr;
}
function ProductList(props: {
  ids: string[];
  categoryIds: number[];
  fullIds: string[];
  fullCategoryIds: number[];
}) {
  const site = useSite();
  const lang = useLang();
  const t = useScopedI18n("产品");
  const { user } = useUser();

  const [selectId, setSelectIds] = useState<string[]>([]);

  const { data: productData } = useSWR(
    ["product_list", props.fullIds, user],
    async ([, ids, user]) => {
      const params = generateLevelParams(
        ["id", "name", "level", "lang", "lang_data.name", "lang_data.lang"],
        "category"
      );

      const fields = [
        "material_number",
        "id",
        "slug",
        "summary",
        "evm_file.*",
        "application_file.*",
      ].concat(params);

      if (user?.role?.level) {
        fields.push(`datasheet${user.role.level}_file.*`);
        fields.push(`datsheet${user.role.level}_text`);
      }

      return fetchCollectionsData<Product>("product", {
        site: site.toString(),
        lang: lang.toString(),
        fields,
        page: 1,
        limit: -1,
        publish: true,
        querys: [
          {
            key: "material_number",
            filter: { _in: ids.join(",") },
          },
        ],
        tags: [
          "product",
          "product_category",
          "application",
          "application_category",
        ],
      });
    },
    {
      revalidateOnFocus: false,
    }
  );

  const { data: categoryData } = useSWR(
    props.fullCategoryIds.length > 0
      ? ["category_list", props.fullCategoryIds, user]
      : null,
    async ([, ids, user]) => {
      const params = generateLevelParams(
        ["id", "name", "level", "lang", "lang_data.name", "lang_data.lang"],
        "category"
      );

      const fields = [
        "material_number",
        "id",
        "slug",
        "summary",
        "evm_file.*",
        "application_file.*",
      ].concat(params);

      if (user?.role?.level) {
        fields.push(`datasheet${user.role.level}_file.*`);
        fields.push(`datsheet${user.role.level}_text`);
      }

      return fetchCollectionsData<Product>("product", {
        site: site.toString(),
        lang: lang.toString(),
        fields,
        page: 1,
        limit: -1,
        querys: [
          {
            key: "category",
            filter: { _in: ids.join(",") },
          },
        ],
        publish: true,
        tags: [
          "product",
          "product_category",
          "application",
          "application_category",
        ],
      });
    },
    {
      revalidateOnFocus: false,
    }
  );

  console.log(productData, 'productData');
  

  const datas = useMemo(() => {
    const datas = productData?.data?.concat(categoryData?.data ?? []);

    // 通过id去重
    const uniqueDatas = uniqBy(datas, "id");

    return uniqueDatas;
  }, [productData?.data, categoryData?.data]);

  const data = useMemo<Product[]>(() => {
    if (datas.length) {
      let product: Product[] = [];

      if (props.categoryIds.length) {
        product.push(
          ...datas.filter((i) => props.categoryIds.includes(i.category?.id))
        );
      }

      if (props.ids.length) {
        product.push(
          ...datas.filter((i) => props.ids.includes(i.material_number))
        );
      }

      return product;
    }

    return datas;
  }, [datas, props.ids, props.categoryIds]);

  const groupedData: CategoryTreeItem[] = useMemo(() => {
    const categorys: CategoryTreeItem[] = [];
    data?.forEach((el) => {
      if (el.category) {
        categorys.push(...getCategoryByLevel(el.category, lang));
      }
    });
    const flatCategorys = uniqBy(categorys, "id");

    flatCategorys.forEach((el, i) => {
      if (el.level === 3) {
        const product = data?.filter((i) => i.category?.id === el.id);

        if (product && product?.length > 0) {
          if (!el.products) {
            flatCategorys[i].products = [];
          }
          flatCategorys[i].products?.push(...product);
        }
      }
    });

    return listToTree(
      flatCategorys,
      { valueKey: "id", parentKey: "parent.id" },
      undefined
    );
  }, [data, lang]);

  // 默认展开所有，获取所有id
  const allIds = useMemo(() => {
    return groupedData.flatMap((l1) =>
      (l1.children || []).map((l2) => l2.id.toString())
    );
  }, [groupedData]);

  useEffect(() => {
    setSelectIds(allIds);
  }, [allIds]);

  console.log(groupedData, 'groupedData');
  

  return (
    <div className="w-full">
      {groupedData.length > 0 && (
        <Accordion
          type="multiple"
          value={selectId}
          onValueChange={setSelectIds}>
          {groupedData.map((l1) => (
            <Fragment key={l1.id}>
              <h5 className="mt-8 text-lg text-primary">
                {getCategoryName(l1, lang)}
              </h5>
              {l1.children?.map((l2) => (
                <AccordionItem
                  key={l2.id}
                  value={l2.id.toString()}
                  className="mt-3 border-none">
                  <AccordionTrigger className="justify-start py-4 text-gray duration-300 hover:bg-zinc-200">
                    <span className="mr-3 text-base text-default">
                      {l2.name}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="flex flex-col gap-y-4 pb-0 pt-1">
                    {l2.products?.map((product) => (
                      <div
                        key={product.id}
                        className="border-b border-b-zinc-200 pb-2">
                        <p className="">
                          <RouterLink
                            href={
                              product.slug
                                ? `/product/${product.slug}`
                                : undefined
                            }
                            target="_blank"
                            className="inline text-sm text-primary hover:opacity-80">
                            {product.material_number}
                          </RouterLink>
                          <Dot
                            color="#333"
                            size={20}
                            className="mx-1 mb-0.5 inline"
                          />
                          <span className="text-xs text-gray">
                            {product.summary}
                          </span>
                        </p>
                        <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                          {user?.role?.level &&
                            product[
                              `datasheet${user.role.level}_file` as "datasheet1_file"
                            ] && (
                              <div className="flex items-center">
                                <span className="text-default">
                                  {product[
                                    `datasheet${user.role.level}_text` as "datasheet1_text"
                                  ] ?? t("数据表")}
                                  :
                                </span>
                                <RouterLink
                                  className="ml-4 text-primary"
                                  href={getFileDownloadAdress(
                                    `${product[
                                      `datasheet${user.role.level}_file` as "datasheet1_file"
                                    ]?.id}/${product[
                                      `datasheet${user.role.level}_file` as "datasheet1_file"
                                    ]?.filename_download}` || ""
                                  )}
                                  target="_blank"
                                  download={
                                    `${product[
                                      `datasheet${user.role.level}_file` as "datasheet1_file"
                                    ]?.filename_download}`
                                  }>
                                  <span className="mr-1 uppercase">
                                    {getSuffixByFileName(
                                      product[
                                        `datasheet${user.role.level}_file` as "datasheet1_file"
                                      ]?.filename_disk || ""
                                    )}
                                  </span>
                                </RouterLink>
                              </div>
                            )}
                          {product.application_file && (
                            <div className="flex items-center">
                              <span className="text-default">
                                {t("应用手册")}:
                              </span>
                              <RouterLink
                                className="ml-4 text-primary"
                                href={getFileDownloadAdress(
                                  `${product.application_file?.id || ""}/${product.application_file?.filename_download || ""}`
                                )}
                                target="_blank"
                                download={
                                  product.application_file?.filename_download
                                }>
                                <span className="mr-1 uppercase">
                                  {getSuffixByFileName(
                                    product.application_file?.filename_disk ||
                                      ""
                                  )}
                                </span>
                              </RouterLink>
                            </div>
                          )}
                          {product.evm_file && (
                            <div className="flex items-center">
                              <span className="text-default">
                                {t("用户指南")}:
                              </span>
                              <RouterLink
                                className="ml-4 text-primary"
                                href={getFileDownloadAdress(
                                  `${product.evm_file?.id || ""}/${product.evm_file?.filename_download || ''}`
                                )}
                                target="_blank"
                                download={product.evm_file?.filename_download}>
                                <span className="mr-1 uppercase">
                                  {getSuffixByFileName(
                                    product.evm_file?.filename_disk || ""
                                  )}
                                </span>
                              </RouterLink>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Fragment>
          ))}
        </Accordion>
      )}
    </div>
  );
}

function GraphItem(props: {
  id: number;
  title: string;
  index: number;
  langData?: { lang: number; title: string }[];
  mobile?: boolean;
}) {
  const t = useScopedI18n("框线图");

  const graphBoxRef = useRef<HTMLDivElement>(null);

  const [scale, setScale] = useState(1);

  const [fullIds, setFullIds] = useState<string[]>([]);

  const [fullCategoryIds, setFullCategoryIds] = useState<number[]>([]);

  const [currentIds, setCurrentIds] = useState<string[]>([]);

  const [currentCategoryIds, setCurrentCategoryIds] = useState<number[]>([]);

  const [full, setFull] = useState(false);
  const langData = useLangData();

  const [areaInfo, setAreaInfo] = useState<{
    name?: Record<string, string>;
    description?: Record<string, string>;
  }>();

  const { data } = useSWR(
    "graph_data" + props.id + props.mobile,
    async () => {
      return fetch(
        `${process.env.NEXT_PUBLIC_DATASERVER}/items/graph/${props.id}`
      ).then((res) => res.json());
    },
    {
      revalidateOnFocus: false,
      onSuccess(data) {
        if (data?.data?.content?.map) {
          const mapsProduct: { graphId: string; productId: string[] }[] =
            data?.data?.content?.map?.filter(
              (i: { graphId: string; productId: string[] }) => {
                return i.productId?.length > 0;
              }
            );

          const mapsCategory: { graphId: string; categoryId: number[] }[] =
            data?.data?.content?.map?.filter(
              (i: { graphId: string; categoryId: string[] }) => {
                return i.categoryId?.length > 0;
              }
            );

          let idsArray: string[] = [];
          let categoryIdsArray: number[] = [];
          if (mapsProduct.length > 0) {
            const productIds = mapsProduct.map((i) => i.productId).flat();
            // 去重
            idsArray.push(...new Set(productIds));

            setCurrentIds(idsArray);
            setFullIds(idsArray);
          }

          if (mapsCategory.length > 0) {
            const categoryIds = mapsCategory.map((i) => i.categoryId).flat();
            // 去重
            categoryIdsArray.push(...new Set(categoryIds));

            setCurrentCategoryIds(categoryIdsArray);
            setFullCategoryIds(categoryIdsArray);
          }

          if (idsArray.length > 0 || categoryIdsArray.length > 0) {
            setFull(true);
          }
        }
      },
    }
  );

  const hotAreaName = langData ? areaInfo?.name?.[langData?.name] : undefined;

  const hotAreaDescription = langData
    ? areaInfo?.description?.[langData?.name]
    : undefined;

  // 计算缩放比例

  const calculateScale = useCallback(
    (full?: boolean) => {
      if (data?.data?.content?.graphs) {
        const width = data?.data?.content?.graphs?.width ?? 1200;
        const boxWidth = graphBoxRef.current?.getBoundingClientRect().width;
        if (width && boxWidth) {
          const scale = boxWidth / width;

          return scale > 1 ? 1 : scale;
        }
      }
    },
    [data?.data]
  );

  const handleResize = useCallback(() => {
    const scale = calculateScale(full);
    if (scale) {
      setScale(scale);
    }
  }, [calculateScale, full]);

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [handleResize]);

  return (
    <div className="w-full max-md:h-full md:px-[110px]">
      <div className="w-full shadow-lg max-md:h-full">
        <h3 className="border-b border-b-zinc-200 bg-white px-11 py-3 text-xl">
          {props.title}
        </h3>
        <div className="flex flex-col md:flex-row">
          <div
            className={cn(
              "grow overflow-x-auto bg-zinc-100 px-6 py-7 duration-300 md:h-full md:w-[65%] md:overflow-hidden md:px-16",
              {
                "w-full": !full,
              }
            )}>
            <div
              ref={graphBoxRef}
              className="relative flex h-auto w-full justify-center overflow-hidden rounded-xl max-md:h-[30vh]">
              <div className="absolute -top-[10vh] bottom-0 left-0 right-0 flex w-full justify-center md:relative md:top-0">
                <div style={{ transform: `scale(${scale})` }}>
                  <GraphPreview
                    graph={data?.data.content.graphs}
                    onClick={(shape) => {
                      const find = data.data.content.map.filter(
                        (i: any) => i.graphId === shape._id
                      ) as (
                        | { graphId: string; productId: string[] }
                        | { graphId: string; categoryId: number[] }
                      )[];
                      if (find) {
                        const productIds = find
                          .filter(
                            (
                              i
                            ): i is { graphId: string; productId: string[] } =>
                              "productId" in i
                          )
                          .flatMap((i) => i.productId);
                        const categoryIds = find
                          .filter(
                            (
                              i
                            ): i is { graphId: string; categoryId: number[] } =>
                              "categoryId" in i
                          )
                          .flatMap((i) => i.categoryId);

                        setCurrentIds(productIds ?? []);
                        setCurrentCategoryIds(categoryIds ?? []);
                      }
                      if (shape._type === "hotarea") {
                        setAreaInfo({
                          name: shape.name,
                          description: shape.description,
                        });
                      }
                      setFull(true);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          {full && (
            <div className="relative flex max-h-[80vh] flex-col bg-white py-5 max-md:h-[35vh] md:w-60 lg:w-80 xl:w-[47%]">
              <div
                className="absolute right-0 top-0 flex cursor-pointer justify-end px-6 pt-4 text-gray hover:text-primary"
                onClick={() => {
                  setFull(false);
                }}>
                <X size={24} />
              </div>
              <h5 className="px-10 text-lg text-zinc-700">{hotAreaName}</h5>
              {hotAreaDescription && (
                <div className="mt-5 px-10">
                  <div className="text-lg text-[#383838]">{t("模块介绍")}</div>
                  <p className="mt-2 text-base text-gray">
                    {hotAreaDescription}
                  </p>
                </div>
              )}
              <div className="w-full flex-auto overflow-y-auto px-10">
                <ProductList
                  ids={currentIds}
                  categoryIds={currentCategoryIds}
                  fullIds={fullIds}
                  fullCategoryIds={fullCategoryIds}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function Graphs(props: {
  data: {
    data?: {
      id: number;
      title: string;
      lang_data?: {
        lang: number;
        title: string;
      }[];
    }[];
    parentSlug?: string;
    mobile?: boolean;
  };
}) {
  const t = useScopedI18n("应用");
  const tCommon = useScopedI18n("通用");
  const { user } = useUser();
  const pathname = usePathname();
  return (
    <div className="relative space-y-4 max-md:flex-auto">
      {user && !props.data.data?.length && (
        <div className="flex flex-col items-center rounded-xl bg-white pb-12 pt-10 shadow-lg md:rounded-none md:pt-14">
          <p className="rexr-sm px-12 text-center text-gray md:text-2xl">
            {t("无框图提示")}
          </p>
          <Image
            className="mt-10 w-44 md:mt-2 md:w-56"
            src={NoDataSVG}
            alt="No Data"
          />
          <RouterLink
            className="mt-6 inline-block"
            href={`/contact-sales?application_category=${props.data?.parentSlug ?? ""}`}>
            <Button className="min-w-[160px] rounded md:min-w-[180px]">
              {tCommon("业务咨询")}
            </Button>
          </RouterLink>
        </div>
      )}
      {/** 未登录提示 */}
      {!user && (
        <div className="flex flex-col items-center rounded-xl bg-white pb-12 pt-10 shadow-lg md:rounded-none md:pt-14">
          <p className="rexr-sm px-12 text-center text-gray md:text-2xl">
            {t("框图未登录提示")}
          </p>
          <Image
            className="mt-10 w-44 md:mt-2 md:w-56"
            src={GoLoginSVG}
            alt="Go login"
          />
          <RouterLink
            className="mt-6 inline-block"
            href={`/login?redirect_url=${encodeURIComponent(pathname)}`}>
            <Button className="min-w-[160px] rounded md:min-w-[180px]">
              {tCommon("立即登录")}
            </Button>
          </RouterLink>
        </div>
      )}
      {user &&
        props.data.data?.map((i, index) => (
          <GraphItem
            index={index + 1}
            id={i.id}
            key={i.id}
            title={i.title}
            langData={i.lang_data}
            mobile={props.data.mobile}
          />
        ))}
    </div>
  );
}
